import { useState, useRef, useEffect, ReactNode, useCallback, ComponentType } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from './supabase';
import { Session } from '@supabase/supabase-js';
import PullToRefresh from 'react-simple-pull-to-refresh';
import Img from '../components/ui/Img';
import { isSignupEnabledByEnv } from '../utils/env';
import { getLogoPath } from '../utils/theme';
import {
  Home,
  Users,
  Users2,
  BookOpen,
  UserCheck,
  GraduationCap,
  DollarSign,
  Building2,
  Tag,
  ClipboardList,
  ChevronDown,
  BarChart3,
  Lightbulb,
  Mail,
  Sparkles,
  Wallet,
  PanelLeftClose,
  PanelLeft,
  Menu,
  X,
  LogOut,
  Settings,
  School,
  UserCircle,
  CalendarOff,
  Search,
} from 'lucide-react';
import { FlagOverridePanel } from '../components/FlagOverridePanel';
import { CommandPalette, Kbd } from './components/CommandPalette';
import { buildPaletteDestinations } from './utils/adminTabs';
import { searchIndexQuery } from './utils/useAdminSearch';
import { isApplePlatform, isSearchShortcut, searchShortcutLabel } from './utils/shortcuts';

interface AdminUser {
  id: string;
  email: string;
  created_at: string;
}

const SIDEBAR_KEY = 'admin-sidebar-open';
const NAV_GROUPS_KEY = 'admin-nav-groups';

export function AdminLayout({ children }: { children: ReactNode }) {
  const [adminUser, setAdminUser] = useState<AdminUser | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState<Session | null>(null);
  const [unauthorizedAccess, setUnauthorizedAccess] = useState(false);
  const [isAlsoStaff, setIsAlsoStaff] = useState(false);
  const [isAlsoParent, setIsAlsoParent] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
    const stored = localStorage.getItem(SIDEBAR_KEY);
    return stored !== null ? stored === 'true' : true;
  });
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isPaletteOpen, setIsPaletteOpen] = useState(false);
  // Only sections the admin has actually toggled land here. Everything absent falls back to
  // "open if it holds the page you are on", so a fresh sidebar shows section names, not a
  // seventeen-item list.
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => {
    try {
      const parsed: unknown = JSON.parse(localStorage.getItem(NAV_GROUPS_KEY) ?? '{}');
      return typeof parsed === 'object' && parsed !== null ? (parsed as Record<string, boolean>) : {};
    } catch {
      return {};
    }
  });
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const dropdownRef = useRef<HTMLDivElement>(null);

  const activeTab = location.pathname.split('/')[2] || 'overview';
  const showSignupEnv = isSignupEnabledByEnv();

  // Collapsed is a rail, not a disappearance: the icons stay so every destination is still one
  // click away and the page does not reflow to full width and back.
  const isRail = !isMobile && !isSidebarOpen;

  // Any event, not just an active one. Gating the nav on an *active* event made the intended
  // workflow impossible: a round is built inactive so the times can be checked before parents
  // see it, but if that is the only event, the tab that would let you review and activate it
  // has already disappeared. The nav still hides for a school that has never run a signup.
  const { data: hasSignupEvent = false } = useQuery({
    queryKey: ['has-signup-event-admin'],
    queryFn: async () => {
      // Only check after session is present; avoid unnecessary calls and keep login screens simple.
      const { data, error } = await supabase
        .from('signup_events')
        .select('id')
        .limit(1);
      if (error) throw error;
      return (data ?? []).length > 0;
    },
    enabled: showSignupEnv && !!session,
  });

  const shouldShowSignupsNav = showSignupEnv && hasSignupEvent;
  const showBalancesNav = true;

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768 || /Mobi|Android/i.test(navigator.userAgent));
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Persist sidebar state (desktop only)
  useEffect(() => {
    if (!isMobile) {
      localStorage.setItem(SIDEBAR_KEY, String(isSidebarOpen));
    }
  }, [isSidebarOpen, isMobile]);

  // Which sections are expanded is a per-admin habit, so it outlives the session.
  useEffect(() => {
    localStorage.setItem(NAV_GROUPS_KEY, JSON.stringify(openGroups));
  }, [openGroups]);

  // Close mobile sidebar on navigation
  useEffect(() => {
    setIsMobileSidebarOpen(false);
  }, [location.pathname]);

  // ⌘K (Ctrl+K off a Mac) opens search from anywhere in the admin app, a focused text field
  // included — the modifier is what keeps it from colliding with typing. Pressed again it
  // closes. It stays out of the way of an open dialog: navigating from search would throw
  // away whatever that dialog was in the middle of editing.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!isSearchShortcut(e)) return;
      const otherDialogOpen = document.querySelector('[aria-modal="true"]:not([data-command-palette])');
      if (otherDialogOpen) return;
      e.preventDefault();
      setIsPaletteOpen(open => !open);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  // Marks the page as the admin app for index.css's touch-screen field sizing (no zoom on
  // focus). On <html> rather than this tree, because some admin dialogs portal to <body>.
  useEffect(() => {
    const root = document.documentElement;
    root.dataset.adminApp = 'true';
    return () => { delete root.dataset.adminApp; };
  }, []);

  // Load search's index a moment after the page has its own data, so the first ⌘K answers as
  // you type instead of waiting on the network. Every page mounts its own layout, but a
  // prefetch of a fresh index is a no-op, so moving between pages does not reload it.
  useEffect(() => {
    if (!adminUser) return;
    const timer = window.setTimeout(() => { void queryClient.prefetchQuery(searchIndexQuery); }, 1500);
    return () => window.clearTimeout(timer);
  }, [adminUser, queryClient]);

  const toggleGroup = useCallback((key: string, isOpen: boolean) => {
    setOpenGroups(prev => ({ ...prev, [key]: !isOpen }));
  }, []);

  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      setIsProfileOpen(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside]);

  // Close profile dropdown when location changes
  useEffect(() => {
    if (isProfileOpen) {
      setIsProfileOpen(false);
    }
  }, [location.pathname]);

  useEffect(() => {
    let mounted = true;

    async function checkSession() {
      try {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();

        if (sessionError) {
          throw sessionError;
        }

        if (!mounted) return;

        setSession(session);

        if (session) {
          // Fetch admin user if we have a session
          try {
            const { data, error: adminError } = await supabase
              .from('admin_users')
              .select('*')
              .limit(1)
              .single();

            if (adminError) {
              // Check if it's a 406 Not Acceptable error (no rows returned)
              if (adminError.code === 'PGRST116' || adminError.message.includes('No rows returned') || adminError.message.includes('406')) {
                setUnauthorizedAccess(true);
                setLoading(false);
                return;
              }
              throw adminError;
            }

            if (!mounted) return;
            setAdminUser(data);
            // Check if admin is also a teacher — awaited so it resolves before setLoading(false)
            const { data: isStaffData } = await supabase.rpc('is_staff');
            if (mounted) setIsAlsoStaff(isStaffData === true);
            // Check if admin also has a parent entity (matched by email) — the
            // Parent Portal link only shows for users who are actually parents.
            const adminEmail = session.user?.email;
            if (adminEmail) {
              const { data: parentRow } = await supabase
                .from('parents')
                .select('id')
                .eq('email', adminEmail)
                .maybeSingle();
              if (mounted) setIsAlsoParent(!!parentRow);
            }
          } catch (apiError: unknown) {
            // Handle JSON parsing errors which might occur when API returns HTML error page
            const errorObj = apiError as { message?: string };
            if (errorObj.message && (
                errorObj.message.includes('JSON') ||
                errorObj.message.includes('Unexpected token') ||
                errorObj.message.includes('SyntaxError')
              )) {
              setUnauthorizedAccess(true);
              setLoading(false);
              return;
            }
            throw apiError;
          }
        }

        setLoading(false);
      } catch (err: unknown) {
        if (!mounted) return;
        console.error('Error checking session:', err);

        // Check for specific error codes related to unauthorized access
        const errorObj = err as { code?: string; message?: string };
        if (
          errorObj.code === 'PGRST116' ||
          (errorObj.message && (
            errorObj.message.includes('406') ||
            errorObj.message.includes('No rows returned') ||
            errorObj.message.includes('JSON') ||
            errorObj.message.includes('Unexpected token')
          ))
        ) {
          setUnauthorizedAccess(true);
        } else {
          setError(errorObj.message || 'An unknown error occurred');
        }
        setLoading(false);
      }
    }

    checkSession();

    return () => {
      mounted = false;
    };
  }, []);

  const handleSignOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      navigate('/');
    } catch (error: unknown) {
      console.error('Error signing out:', error);
      const errorObj = error as { message?: string };
      setError(errorObj.message || 'An unknown error occurred');
    }
  };

  const handleGoToMainApp = () => {
    window.location.href = '/dashboard';
  };

  const handleRefresh = () => {
    window.location.reload();
    return Promise.resolve();
  };

  const userFullName = session?.user?.user_metadata?.full_name || 'Admin';
  const userEmail = session?.user?.email || '';
  const userAvatar = session?.user?.user_metadata?.avatar_url || session?.user?.user_metadata?.picture;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
          <div className="text-xl text-gray-600">Loading...</div>
        </div>
      </div>
    );
  }

  if (unauthorizedAccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center p-8 bg-white shadow-lg rounded-lg max-w-md">
          <div className="flex justify-center mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Access Denied</h2>
          <p className="text-gray-600 mb-6">Your account does not have administrator privileges required to access this area.</p>
          <div className="flex flex-col space-y-3">
            <button
              onClick={handleGoToMainApp}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
            >
              Return to Home Page
            </button>
            <button
              onClick={() => window.location.reload()}
              className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center p-4 bg-white shadow rounded-lg">
          <h2 className="text-xl text-red-600 mb-2">Error</h2>
          <p>{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!session || !adminUser) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center p-4 bg-white shadow rounded-lg">
          <h2 className="text-xl mb-4">Admin Login</h2>
          <button
            onClick={() => navigate('/admin-app')}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  type NavLink = {
    key: string;
    label: string;
    icon: ComponentType<{ className?: string }>;
    show: boolean;
    href?: string;
  };
  type NavGroup = { key: string; label: string; items: NavLink[] };

  // Overview and Genie are the two "where do I start" destinations, so they stay at the top
  // level. Everything else lives under a named section: seventeen peers separated by hairlines
  // read as one long list, and the hairlines never said what the groups were.
  const topNavItems: NavLink[] = [
    { key: 'overview',     label: 'Overview', icon: Home,     show: true },
    { key: 'agent-search', label: 'Genie',    icon: Sparkles, show: true },
  ].filter(item => item.show);

  const allNavGroups: NavGroup[] = [
    {
      key: 'people',
      label: 'People',
      items: [
        { key: 'students', label: 'Students',        icon: Users,         show: true },
        { key: 'parents',  label: 'Parents',         icon: Users2,        show: true },
        { key: 'teachers', label: 'Teachers',        icon: GraduationCap, show: true },
        { key: 'signups',  label: 'Meeting Signups', icon: ClipboardList, show: shouldShowSignupsNav },
      ],
    },
    {
      key: 'programs',
      label: 'Programs',
      items: [
        { key: 'programs',        label: 'Programs',        icon: BookOpen,    show: true },
        { key: 'enrollments',     label: 'Enrollments',     icon: UserCheck,   show: true },
        { key: 'school-calendar', label: 'School Calendar', icon: CalendarOff, show: true },
      ],
    },
    {
      key: 'finance',
      label: 'Finance',
      items: [
        { key: 'finances',      label: 'Finance Ops',    icon: DollarSign, show: true },
        { key: 'revenue-share', label: 'Finance Report', icon: Building2,  show: true },
        { key: 'coupons',       label: 'Discounts',      icon: Tag,        show: true },
        { key: 'balances',      label: 'Balances',       icon: Wallet,     show: showBalancesNav },
      ],
    },
    {
      key: 'reporting',
      label: 'Reporting',
      items: [
        { key: 'insights',  label: 'Insights',  icon: Lightbulb, show: true },
        { key: 'analytics', label: 'Analytics', icon: BarChart3, show: true },
      ],
    },
    {
      key: 'operations',
      label: 'Operations',
      items: [
        { key: 'email',  label: 'Email',  icon: Mail,     show: true },
        { key: 'system', label: 'System', icon: Settings, show: true },
      ],
    },
  ];

  const navGroups = allNavGroups
    .map(group => ({ ...group, items: group.items.filter(item => item.show) }))
    .filter(group => group.items.length > 0);

  // Portal-switch links are pinned to the bottom of the nav; the rest scroll.
  const portalNavItems: NavLink[] = [
    { key: 'teacher-portal', label: 'Teacher Portal', icon: School,     show: isAlsoStaff,  href: '/staff-app' },
    { key: 'parent-portal',  label: 'Parent Portal',  icon: UserCircle, show: isAlsoParent, href: '/dashboard' },
  ].filter(item => item.show);

  // Search reaches exactly what the sidebar does, under the same names, plus each page's
  // tabs — so a page that is hidden from the nav (Meeting Signups before any round exists)
  // is not findable either.
  const paletteDestinations = buildPaletteDestinations([
    ...topNavItems.map(item => ({ key: item.key, label: item.label, section: '', icon: item.icon })),
    ...navGroups.flatMap(group =>
      group.items.map(item => ({ key: item.key, label: item.label, section: group.label, icon: item.icon })),
    ),
  ]);

  const openSearchFromSidebar = () => {
    // On a phone the drawer would otherwise stay open over the page search lands on.
    setIsMobileSidebarOpen(false);
    setIsPaletteOpen(true);
  };

  // A box, not a nav row, so it reads as "type here". The shortcut badge is what teaches ⌘K;
  // a phone has no keyboard to press it on, so it gets the box alone.
  const renderSearchButton = () => (
    <button
      type="button"
      onClick={openSearchFromSidebar}
      title={isRail ? `Search (${searchShortcutLabel()})` : undefined}
      aria-label="Search pages, people, enrollments and invoices"
      aria-keyshortcuts={isApplePlatform() ? 'Meta+K' : 'Control+K'}
      className={`flex items-center w-full mb-2 rounded-lg text-sm transition-colors ${
        isRail
          ? 'justify-center py-2 text-gray-600 hover:bg-gray-50 hover:text-gray-900'
          : 'px-3 py-1.5 border border-gray-200 bg-gray-50 text-gray-500 hover:bg-gray-100 hover:text-gray-700'
      }`}
    >
      <Search className={isRail ? 'h-[18px] w-[18px] flex-shrink-0' : 'h-4 w-4 flex-shrink-0'} />
      {!isRail && <span className="ml-2 flex-1 text-left">Search</span>}
      {!isRail && !isMobile && <Kbd>{searchShortcutLabel()}</Kbd>}
    </button>
  );

  const renderNavButton = (item: NavLink, nested = false) => {
    const isActive = !item.href && activeTab === item.key;
    const Icon = item.icon;
    return (
      <button
        key={item.key}
        onClick={() => item.href ? (window.location.href = item.href) : navigate(`/admin-app/${item.key}`)}
        title={isRail ? item.label : undefined}
        aria-label={isRail ? item.label : undefined}
        aria-current={isActive ? 'page' : undefined}
        className={`flex items-center w-full py-2 mb-0.5 rounded-lg text-sm transition-colors ${
          isRail ? 'justify-center px-0' : nested ? 'pl-6 pr-3' : 'px-3'
        } ${
          item.href
            ? 'bg-indigo-50 text-indigo-700 font-medium hover:bg-indigo-100'
            : isActive
            ? 'bg-gray-100 text-gray-900 font-semibold'
            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
        }`}
      >
        {Icon && <Icon className="h-[18px] w-[18px] flex-shrink-0" />}
        {!isRail && <span className="ml-3">{item.label}</span>}
      </button>
    );
  };

  const renderNavGroup = (group: NavGroup) => {
    const hasActive = group.items.some(item => !item.href && activeTab === item.key);
    // Untouched sections follow the page: the one you are in opens, the rest stay shut.
    const isOpen = openGroups[group.key] ?? hasActive;
    return (
      <div key={group.key}>
        <button
          type="button"
          onClick={() => toggleGroup(group.key, isOpen)}
          aria-expanded={isOpen}
          aria-controls={`nav-group-${group.key}`}
          className={`flex items-center w-full px-3 py-1.5 mb-0.5 rounded-lg text-[11px] font-semibold uppercase tracking-wider transition-colors ${
            hasActive && !isOpen
              ? 'text-gray-900 bg-gray-100'
              : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'
          }`}
        >
          <span className="flex-1 text-left">{group.label}</span>
          <ChevronDown className={`h-3.5 w-3.5 flex-shrink-0 transition-transform ${isOpen ? '' : '-rotate-90'}`} />
        </button>
        {isOpen && (
          <div id={`nav-group-${group.key}`}>
            {group.items.map(item => renderNavButton(item, true))}
          </div>
        )}
      </div>
    );
  };

  const sidebarContent = (
    <>
      {/* Logo area */}
      <div className={`flex items-center h-14 border-b border-gray-200 flex-shrink-0 ${isRail ? 'justify-center px-0' : 'px-4'}`}>
        <Img
          src={getLogoPath()}
          alt="BHCS Logo"
          className="h-7 w-auto"
          width={512}
          height={512}
        />
        {!isRail && <span className="ml-2 text-lg font-bold text-gray-900">Admin</span>}
        {/* Close button for mobile */}
        {isMobile && (
          <button
            onClick={() => setIsMobileSidebarOpen(false)}
            className="ml-auto p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
            aria-label="Close sidebar"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-2 px-2 flex flex-col">
        {/* A 64px rail has no room for section names, so it falls back to the flat icon list
            it has always been, with a hairline where each section starts. */}
        {isRail ? (
          <div>
            {renderSearchButton()}
            {topNavItems.map(item => renderNavButton(item))}
            {navGroups.map(group => (
              <div key={group.key}>
                <hr className="my-1 mx-1 border-gray-100" />
                {group.items.map(item => renderNavButton(item))}
              </div>
            ))}
          </div>
        ) : (
          <div>
            {renderSearchButton()}
            {topNavItems.map(item => renderNavButton(item))}
            <div className="mt-3 space-y-1">{navGroups.map(renderNavGroup)}</div>
          </div>
        )}
      </nav>

      {/* Portal-switch links: pinned OUTSIDE the scrollable nav so they are
          always visible, never clipped by the scroll area on short viewports. */}
      {portalNavItems.length > 0 && (
        <div className="flex-shrink-0 px-2 py-1 border-t border-gray-200">
          {portalNavItems.map(item => renderNavButton(item))}
        </div>
      )}

      {/* User profile at bottom, with the feature-flag toggle in the same row */}
      <div className={`border-t border-gray-200 flex-shrink-0 ${isRail ? 'p-2' : 'p-3'}`} ref={dropdownRef}>
        <div className="relative flex items-center gap-1">
          <button
            onClick={() => setIsProfileOpen(!isProfileOpen)}
            title={isRail ? userFullName : undefined}
            className={`flex items-center flex-1 min-w-0 rounded-lg hover:bg-gray-100 transition-colors ${
              isRail ? 'justify-center p-1' : 'p-2'
            }`}
            aria-haspopup="menu"
            aria-expanded={isProfileOpen}
            aria-label="Open profile menu"
          >
            {userAvatar ? (
              <Img
                src={userAvatar}
                alt={userFullName}
                className="h-8 w-8 rounded-full flex-shrink-0"
                width={32}
                height={32}
              />
            ) : (
              <div className="h-8 w-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                <span className="text-sm font-semibold text-indigo-700">
                  {userFullName.charAt(0).toUpperCase()}
                </span>
              </div>
            )}
            {!isRail && (
              <>
                <div className="ml-3 text-left min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-900 truncate">{userFullName}</p>
                </div>
                <ChevronDown className={`h-4 w-4 text-gray-400 flex-shrink-0 transition-transform ${isProfileOpen ? 'rotate-180' : ''}`} />
              </>
            )}
          </button>

          {/* The flag panel needs its label row to make sense, so it waits for the full sidebar. */}
          {!isRail && <FlagOverridePanel canToggleFlags />}

          {isProfileOpen && (
            <div className={`absolute bottom-full mb-1 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50 ${
              isRail ? 'left-0 w-56' : 'left-0 right-0'
            }`}>
              <p className="px-4 py-2 text-xs text-gray-500 truncate border-b border-gray-100">{userEmail}</p>
              <button
                onClick={handleSignOut}
                className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                <LogOut className="h-4 w-4 mr-2 text-gray-400" />
                Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );

  return (
    <PullToRefresh onRefresh={handleRefresh} isPullable={isMobile}>
      <div className={`${isMobile ? 'min-h-screen' : 'h-screen overflow-hidden'} bg-gray-50 flex`}>
        {/* Skip to content link */}
        <a href="#admin-main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 bg-white text-blue-600 px-3 py-2 rounded shadow">
          Skip to content
        </a>

        {/* Desktop sidebar */}
        {!isMobile && (
          <aside
            className={`fixed top-0 left-0 bottom-0 z-40 bg-white border-r border-gray-200 flex flex-col transition-all duration-300 ease-in-out ${
              isSidebarOpen ? 'w-60' : 'w-16'
            } ${isProfileOpen ? '' : 'overflow-hidden'}`}
          >
            {/* Clipping is what keeps the 240px-wide inner wrapper from spilling out while the
                rail animates back open — but it also cropped the profile menu, which is wider
                than the 64px rail it hangs off. Drop the clip while that menu is open: the
                sidebar is not mid-transition then, because opening it closes the menu first. */}
            {/* Inner wrapper to prevent content from shrinking */}
            <div className={`h-full flex flex-col ${isSidebarOpen ? 'w-60 min-w-[15rem]' : 'w-16 min-w-[4rem]'}`}>
              {sidebarContent}
            </div>
          </aside>
        )}

        {/* Desktop sidebar toggle */}
        {!isMobile && (
          <button
            onClick={() => setIsSidebarOpen(prev => !prev)}
            className={`fixed top-3 z-50 p-2 rounded-lg bg-white border border-gray-200 shadow-sm text-gray-500 hover:text-gray-700 hover:bg-gray-50 transition-all duration-300 ease-in-out ${
              isSidebarOpen ? 'left-[15.5rem]' : 'left-[4.5rem]'
            }`}
            aria-label={isSidebarOpen ? 'Close sidebar' : 'Open sidebar'}
          >
            {isSidebarOpen ? <PanelLeftClose className="h-5 w-5" /> : <PanelLeft className="h-5 w-5" />}
          </button>
        )}

        {/* Mobile backdrop */}
        {isMobile && isMobileSidebarOpen && (
          <div
            className="fixed inset-0 z-40 bg-black/30 backdrop-blur-sm transition-opacity"
            onClick={() => setIsMobileSidebarOpen(false)}
          />
        )}

        {/* Mobile sidebar */}
        {isMobile && (
          <aside
            className={`fixed top-0 left-0 bottom-0 z-50 bg-white w-72 flex flex-col shadow-xl transition-transform duration-300 ease-in-out ${
              isMobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'
            }`}
          >
            {sidebarContent}
          </aside>
        )}

        {/* Main content area */}
        <div
          className={`flex-1 min-w-0 transition-all duration-300 ease-in-out ${
            isMobile ? 'ml-0' : isSidebarOpen ? 'ml-60' : 'ml-16'
          } ${isMobile ? 'overflow-y-auto' : 'flex flex-col overflow-hidden'}`}
        >
          {/* Mobile top bar */}
          {isMobile && (
            <div className="fixed top-0 left-0 right-0 z-30 bg-white border-b border-gray-200 flex items-center h-14 px-4">
              <button
                onClick={() => setIsMobileSidebarOpen(true)}
                className="p-2 -ml-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                aria-label="Open menu"
              >
                <Menu className="h-5 w-5" />
              </button>
              <div className="ml-3 flex items-center">
                <Img
                  src={getLogoPath()}
                  alt="BHCS Logo"
                  className="h-6 w-auto"
                  width={512}
                  height={512}
                />
                <span className="ml-2 text-base font-bold text-gray-900">Admin</span>
              </div>
            </div>
          )}

          {/* Page content */}
          <main id="admin-main-content" role="main" className={`px-4 sm:px-6 lg:px-12 ${isMobile ? 'pt-20 pb-6' : 'py-6 flex flex-col flex-1 min-h-0'}`}>
            {children}
          </main>
        </div>

        <CommandPalette
          open={isPaletteOpen}
          onClose={() => setIsPaletteOpen(false)}
          destinations={paletteDestinations}
        />
      </div>
    </PullToRefresh>
  );
}
